import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash, Hash, MapPinArea, Truck } from "@phosphor-icons/react";
import { toast } from "sonner";
import GeofenceAssignmentsEditor from "./GeofenceAssignmentsEditor";
import DeviceAssignmentsEditor from "./DeviceAssignmentsEditor";
import DateTimePicker from "./DateTimePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Customer-provided shipment identifiers. The platform generates only the
// internal shipment_id (UUID); every other operational identifier comes from
// the customer's ERP/TMS and is searchable across the platform.
const REF_TYPES = [
  "PO", "Order", "BOL", "AWB", "Container", "Load", "Pickup",
  "Manifest", "Delivery Order", "Master Tracking", "Shipment",
  "Work Order", "Customer Reference", "Custom",
];

// Placeholder hints for the most common identifier patterns.
const REF_PLACEHOLDER = {
  "PO":               "PO-12345",
  "Order":            "ORD-67890",
  "BOL":              "BOL-554433",
  "AWB":              "176-22334455",
  "Container":        "MSCU1234567",
  "Load":             "LD-9988",
  "Pickup":           "PU-44221",
  "Manifest":         "MAN-0001",
  "Delivery Order":   "DO-0042",
  "Master Tracking":  "1Z999AA10123456784",
  "Shipment":         "SH-2024-001",
  "Work Order":       "WO-7700",
  "Customer Reference": "CUST-REF-XYZ",
  "Custom":           "Reference value",
};

export default function ShipmentForm({ open, onClose, onCreated }) {
  const [form, setForm] = useState(initialForm());
  const [devices, setDevices] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initialForm());
    axios.get(`${API}/fleet/devices?limit=500`)
      .then((res) => setDevices(res.data.devices || []))
      .catch(() => setDevices([]));
    axios.get(`${API}/parties/facilities`)
      .then((res) => setFacilities(res.data?.items || res.data || []))
      .catch(() => setFacilities([]));
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addRef = () => set("references", [...form.references, { type: "PO", value: "", label: "" }]);
  const updateRef = (i, key, val) => {
    const refs = [...form.references];
    refs[i] = { ...refs[i], [key]: val };
    set("references", refs);
  };
  const removeRef = (i) => set("references", form.references.filter((_, idx) => idx !== i));

  const toggleDevice = (name) => {
    const cur = new Set(form.device_names);
    if (cur.has(name)) cur.delete(name); else cur.add(name);
    set("device_names", Array.from(cur));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Shipment name is required"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        references: form.references.filter((r) => r.value.trim()),
        temp_min: parseFloat(form.temp_min) || 32,
        temp_max: parseFloat(form.temp_max) || 40,
      };
      // Hydrate origin / destination from the chosen facility ids so the
      // backend has lat/lng, address, and the linked facility_id ready.
      const hydrate = (facId) => {
        if (!facId) return null;
        const f = facilities.find((x) => x.id === facId);
        if (!f) return null;
        return {
          facility_id: f.id,
          name:        f.name,
          address:     f.address || "",
          city:        f.city || "",
          state:       f.state || "",
          country:     f.country || "",
          lat:         f.latitude ?? f.lat ?? null,
          lng:         f.longitude ?? f.lng ?? null,
          radius_m:    f.radius_m || 250,
        };
      };
      if (form.origin_facility_id)      payload.origin      = hydrate(form.origin_facility_id);
      if (form.destination_facility_id) payload.destination = hydrate(form.destination_facility_id);
      delete payload.origin_facility_id;
      delete payload.destination_facility_id;
      // Strip our local-only fields so the backend Shipment model doesn't reject them.
      delete payload.geofence_assignments;
      delete payload.device_registry_ids;

      const res = await axios.post(`${API}/shipments/`, payload);
      const sid = res.data?.id;

      // Flush pending geofence + device assignments now that we have a shipment id.
      const pendingGfs = form.geofence_assignments || [];
      const pendingDevs = form.device_registry_ids || [];
      if (sid && (pendingGfs.length || pendingDevs.length)) {
        try {
          await Promise.all([
            ...pendingGfs.map((a) =>
              axios.post(`${API}/geofences/assign-to-shipment/${sid}`, {
                geofence_id: a.geofence_id, role: a.role, label: a.label || null,
              })
            ),
            ...pendingDevs.map((id) =>
              axios.post(`${API}/device-registry/${id}/assign-shipment`, { shipment_id: sid })
            ),
          ]);
        } catch (err) {
          toast.warning("Shipment created, but some assignments failed to attach");
        }
      }
      onCreated?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create shipment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="shipment-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-100">New Shipment</DialogTitle>
          <p className="text-xs text-zinc-500 font-mono mt-1">A unique Shipment ID will be auto-generated for tracking.</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)}
                placeholder="e.g. Vaccine Shipment to Boston"
                className="bg-zinc-900 border-zinc-800 text-sm mt-1"
                data-testid="ship-form-name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Customer</Label>
              <Input value={form.customer} onChange={(e) => set("customer", e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-sm mt-1"
                placeholder="CornerStone Technologies"
                data-testid="ship-form-customer" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Cargo Type</Label>
              <Input value={form.cargo_type} onChange={(e) => set("cargo_type", e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-sm mt-1"
                placeholder="Pharma, Food, etc."
                data-testid="ship-form-cargo" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Driver</Label>
              <Input value={form.driver_name} onChange={(e) => set("driver_name", e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-sm mt-1"
                data-testid="ship-form-driver" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">ETA (optional)</Label>
              <div className="mt-1">
                <DateTimePicker value={form.eta}
                  onChange={(iso) => set("eta", iso)}
                  placeholder="Pick an ETA"
                  testId="ship-form-eta" />
              </div>
            </div>
          </div>

          {/* Origin / Destination — facility pickers */}
          <div className="border border-zinc-800 rounded-sm p-3 bg-zinc-900/30">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400 flex items-center gap-1">
                <Truck size={12} /> Origin & Destination
              </Label>
              <button type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("holotrak:nav", { detail: { target: "facilities" } }))}
                className="text-[11px] text-teal-400 hover:text-teal-300 flex items-center gap-1"
                data-testid="ship-form-add-facility">
                <Plus size={11} /> Add Facility
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FacilityPicker
                role="origin"
                value={form.origin_facility_id}
                onChange={(v) => set("origin_facility_id", v)}
                facilities={facilities}
              />
              <FacilityPicker
                role="destination"
                value={form.destination_facility_id}
                onChange={(v) => set("destination_facility_id", v)}
                facilities={facilities}
              />
            </div>
            <p className="text-[10px] text-zinc-600 mt-2">
              Facilities are managed under <strong>Admin Tools &rarr; Parties</strong>. Adding a
              facility address auto-creates a 250&nbsp;m geofence (radius adjustable there).
            </p>
          </div>

          {/* References */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400 flex items-center gap-1">
                <Hash size={11} /> References (PO, Order, BOL, etc.)
              </Label>
              <Button type="button" variant="outline" size="sm" onClick={addRef}
                className="border-zinc-700 hover:bg-zinc-800 text-xs h-7"
                data-testid="add-reference-btn">
                <Plus size={12} className="mr-1" /> Add Reference
              </Button>
            </div>
            <div className="space-y-2">
              {form.references.length === 0 ? (
                <p className="text-[10px] text-zinc-600 italic">No references attached. Add a PO, Order #, or any custom identifier for ERP/TMS tracking.</p>
              ) : form.references.map((r, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`reference-row-${i}`}>
                  <select value={r.type} onChange={(e) => updateRef(i, "type", e.target.value)}
                    className="bg-zinc-900 border border-zinc-800 text-zinc-200 text-xs rounded-sm px-2 h-8 w-24 font-mono"
                    data-testid={`reference-type-${i}`}>
                    {REF_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {r.type === "Custom" && (
                    <Input value={r.label} onChange={(e) => updateRef(i, "label", e.target.value)}
                      placeholder="Label" className="bg-zinc-900 border-zinc-800 text-xs h-8 w-32" />
                  )}
                  <Input value={r.value} onChange={(e) => updateRef(i, "value", e.target.value)}
                    placeholder={REF_PLACEHOLDER[r.type] || "Reference value"}
                    className="bg-zinc-900 border-zinc-800 text-xs h-8 flex-1 font-mono"
                    data-testid={`reference-value-${i}`} />
                  <button type="button" onClick={() => removeRef(i)}
                    className="text-zinc-500 hover:text-red-400 p-1.5"
                    data-testid={`remove-reference-${i}`}>
                    <Trash size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Devices (legacy: telemetry-derived) */}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2 block">
              Telemetry Devices ({form.device_names.length} selected)
            </Label>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm max-h-32 overflow-y-auto p-1">
              {devices.length === 0 ? (
                <p className="text-[10px] text-zinc-600 p-2">No telemetry-derived devices yet.</p>
              ) : devices.map((d) => (
                <label key={d.device_name}
                  className="flex items-center gap-2 px-2 py-1 hover:bg-zinc-800 rounded-sm cursor-pointer"
                  data-testid={`device-checkbox-${d.device_name}`}>
                  <input type="checkbox" checked={form.device_names.includes(d.device_name)}
                    onChange={() => toggleDevice(d.device_name)}
                    className="accent-teal-500" />
                  <span className="text-xs font-mono text-zinc-200">{d.device_name}</span>
                  <span className="text-[10px] text-zinc-500 ml-auto">{d.status}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Devices (canonical: Device Registry) */}
          <DeviceAssignmentsEditor
            value={form.device_registry_ids}
            onChange={(v) => set("device_registry_ids", v)}
          />

          {/* Geofence assignments */}
          <GeofenceAssignmentsEditor
            value={form.geofence_assignments}
            onChange={(v) => set("geofence_assignments", v)}
          />

          {/* Temp range */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Temp Min (°F)</Label>
              <Input type="number" value={form.temp_min} onChange={(e) => set("temp_min", e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-sm mt-1 font-mono"
                data-testid="ship-form-temp-min" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Temp Max (°F)</Label>
              <Input type="number" value={form.temp_max} onChange={(e) => set("temp_max", e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-sm mt-1 font-mono"
                data-testid="ship-form-temp-max" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs text-zinc-300 mb-2">
                <input type="checkbox" checked={form.signature_required}
                  onChange={(e) => set("signature_required", e.target.checked)}
                  className="accent-teal-500"
                  data-testid="ship-form-signature-req" />
                Signature on delivery
              </label>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-sm mt-1 min-h-[60px]"
              placeholder="Optional handling instructions..."
              data-testid="ship-form-notes" />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800">
            <Button type="button" variant="outline" onClick={onClose} className="border-zinc-700 text-xs h-9">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}
              className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-9"
              data-testid="ship-form-submit">
              {submitting ? "Creating..." : "Create Shipment"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function initialForm() {
  return {
    name: "",
    customer: "",
    cargo_type: "Pharma",
    references: [],
    device_names: [],
    device_registry_ids: [],
    geofence_assignments: [],
    driver_name: "",
    eta: "",
    temp_min: 32,
    temp_max: 40,
    signature_required: false,
    notes: "",
    origin_facility_id: "",
    destination_facility_id: "",
  };
}

/**
 * FacilityPicker — single-select for Origin or Destination.
 * If no facilities exist yet, points the operator at the Parties module.
 */
function FacilityPicker({ role, value, onChange, facilities }) {
  const label = role === "origin" ? "Origin" : "Destination";
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
        <MapPinArea size={10} weight="duotone" /> {label}
      </Label>
      {facilities.length === 0 ? (
        <div className="mt-1 p-2 border border-dashed border-zinc-800 rounded-sm text-[11px] text-zinc-500">
          No facilities yet.{" "}
          <button type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("holotrak:nav", { detail: { target: "facilities" } }))}
            className="text-teal-400 hover:text-teal-300 underline">
            Create one
          </button>{" "}
          under Admin Tools &rarr; Parties.
        </div>
      ) : (
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full bg-zinc-900 border border-zinc-800 text-zinc-200 text-xs rounded-sm h-9 px-2"
          data-testid={`ship-form-${role}-facility`}
        >
          <option value="">— Select facility —</option>
          {facilities.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}{f.city ? ` · ${f.city}` : ""}{f.type ? ` (${f.type})` : ""}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
